import { processImage } from '../util/image_processing.js';
import { window } from '../window.js';
import { ImageList, ImageListItem } from './image_list.js';

import './image_close_button.js';

/**
 * The editable list of images.
 **/
class ImageEditableList extends ImageList {
  set(list) {
    for (let src of list) {
      this.appendItem(src);
    }
  }

  constructor() {
    super();

    this.draggedItem = null;
    this.lastX = 0;
  }

  initialize() {
    super.initialize();

    this.addEventListener('dragstart', ev => this.onDragStart(ev));
    this.addEventListener('dragend', ev => this.onDragEnd(ev));
    this.addEventListener('dragover', ev => this.onDragOver(ev));
    this.addEventListener('drop', ev => this.onDrop(ev));

    this.fileInputElement.addEventListener('change', ev => {
      this.dispatchItemInsertEvent(ev.target.files);
      ev.target.value = '';
    });

    this.imageAddElement.addEventListener('drag', ev => {
      ev.preventDefault();
      ev.stopPropagation();
    });

    this.imageAddElement.addEventListener('dragstart', ev => {
      ev.preventDefault();
      ev.stopPropagation();
    });

    this.imageAddElement.addEventListener('dragover', ev => {
      ev.preventDefault();
      ev.stopPropagation();
      this.imageAddElement.classList.add('dragover');
    });

    this.imageAddElement.addEventListener('dragenter', ev => {
      ev.preventDefault();
      ev.stopPropagation();
      this.imageAddElement.classList.add('dragover');
    });

    this.imageAddElement.addEventListener('dragleave', ev => {
      ev.preventDefault();
      ev.stopPropagation();
      this.imageAddElement.classList.remove('dragover');
    });

    this.imageAddElement.addEventListener('dragend', ev => {
      ev.preventDefault();
      ev.stopPropagation();
      this.imageAddElement.classList.remove('dragover');
    });

    this.imageAddElement.addEventListener('drop', ev => {
      ev.preventDefault();
      ev.stopPropagation();
      this.dispatchItemInsertEvent(ev.dataTransfer.files);
    });
  }

  static get markup() {
    return `
      <div class='image-placeholder image-insertion-point'></div>
      <label class='image-add'>
        <input type='file' multiple accept='image/*' hidden>
        <span class='image-add-plus'>+</span>
      </label>`;
  }

  appendItem(src) {
    let srcAttribute = (src && `src='${src}'`) || '';
    this.insPointElement.insertAdjacentHTML(
      'beforebegin',
      `<div class='image-placeholder'></div>
       <image-editable-list-item class='image-item'
                                ${srcAttribute}></image-editable-list-item>`
    );
    let itemEl = this.insPointElement.previousElementSibling;
    itemEl.addEventListener('close', ev =>
      this.dispatchItemRemoveEvent(ev.target)
    );
    return itemEl;
  }

  async dispatchItemInsertEvent(files) {
    let itemsPromise = Promise.all(
      Array.from(files).map(file => {
        let item = this.appendItem();
        return processImage(file).then(info => {
          item.src = info.url;
          return {
            info,
            item,
          };
        });
      })
    );
    this.scrollLeft = this.imageAddElement.getBoundingClientRect().left;

    let detail = await itemsPromise;
    this.dispatchEvent(
      new CustomEvent('iteminsert', {
        detail,
      })
    );
  }

  dispatchItemRemoveEvent(itemElement) {
    let detail = {
      item: itemElement,
      index: itemElement.index,
    };
    itemElement.remove();
    this.dispatchEvent(
      new CustomEvent('itemremove', {
        detail,
      })
    );
  }

  dispatchItemReorderEvent(oldIndex, newIndex) {
    this.dispatchEvent(
      new CustomEvent('itemreorder', {
        detail: {
          oldIndex,
          newIndex,
        },
      })
    );
  }

  onDragStart(ev) {
    this.draggedItem = ev.target.closest('image-editable-list-item');
    if (this.draggedItem) {
      this.draggedItem.classList.add('dragged');
    }
  }

  onDragEnd() {
    if (this.draggedItem) {
      this.draggedItem.classList.remove('dragged');
    }
    this.clearDropTarget();
    this.draggedItem = null;
  }

  onDragOver(ev) {
    ev.preventDefault();

    // Ignore dragover if the move wasn't long enough or no pic is dragged.
    if (Math.abs(this.lastX - ev.clientX) < 50 || !this.draggedItem) {
      return;
    }

    let overPic = ev.target.closest('image-editable-list-item');
    if (overPic && overPic != this.draggedItem) {
      let rect = overPic.getBoundingClientRect();
      let offset = ev.clientX - rect.x;
      if (offset < rect.width / 2 && this.isLeft != true) {
        if (this.prevTo(overPic) != this.draggedItem) {
          this.setDropTargetPrevTo(overPic, ev);
          this.isLeft = true;
          this.lastX = ev.clientX;
        }
      } else if (offset > rect.width / 2 && this.isLeft != false) {
        if (this.nextTo(overPic) != this.draggedItem) {
          this.setDropTargetNextTo(overPic, ev);
        } else {
          this.clearDropTarget();
          this.isLeft = false;
          this.lastX = ev.clientX;
        }
      }
    }
  }

  onDrop(ev) {
    if (this.draggedItem && ev.target.classList.contains('droptarget')) {
      let oldIndex = this.draggedItem.index;
      let picPlaceholder = this.draggedItem.previousElementSibling;
      ev.target.parentNode.insertBefore(this.draggedItem, ev.target);
      ev.target.parentNode.insertBefore(picPlaceholder, this.draggedItem);

      // No animation for droptarget hide.
      ev.target.hidden = true;
      ev.target.classList.remove('droptarget');
      window.setTimeout(() => {
        ev.target.hidden = false;
      }, 500);

      this.dispatchItemReorderEvent(oldIndex, this.draggedItem.index);
    }
  }

  nextTo(imageEl) {
    let el = imageEl.nextElementSibling.nextElementSibling;
    return (el != this.insPointElement && el) || null;
  }

  isLast(imageEl) {
    return !!this.nextTo(imageEl);
  }

  prevTo(imageEl) {
    return imageEl.previousElementSibling.previousElementSibling;
  }

  setDropTargetNextTo(imageEl, ev) {
    this.setDropTarget(imageEl.nextElementSibling, ev);
  }

  setDropTargetPrevTo(imageEl, ev) {
    this.setDropTarget(imageEl.previousElementSibling, ev);
  }

  get dropTarget() {
    return this.querySelector('.droptarget');
  }

  clearDropTarget() {
    let el = this.dropTarget;
    if (el) {
      el.classList.remove('droptarget');
    }
  }

  setDropTarget(dropTarget, ev) {
    if (dropTarget.classList.contains('droptarget')) {
      return;
    }

    this.clearDropTarget();
    dropTarget.classList.add('droptarget');
    this.lastX = ev.clientX;

    this.scrollDropTarget(dropTarget);

    // wait until transformation is done
    window.setTimeout(() => this.scrollDropTarget(dropTarget), 250);
    window.setTimeout(() => this.scrollDropTarget(dropTarget), 500);
  }

  scrollDropTarget(dropTarget) {
    let { right } = dropTarget.getBoundingClientRect();
    let { width } = this.getBoundingClientRect();
    if (right > width) {
      this.scrollBy(right - width, 0);
    }
  }

  static get selectors() {
    const AllItems = 'image-editable-list-item';
    return {
      AllItems,
      FileInput: 'input',
      ImageAdd: '.image-add',
      InsPoint: '.image-insertion-point',
      ItemAt(index) {
        return `${AllItems}:nth-of-type(${index + 1})`;
      },
      SelectedItem: `${AllItems}[selected]`,
    };
  }

  static get elements() {
    let s = this.selectors;
    return {
      fileInputElement: s.FileInput,
      imageAddElement: s.ImageAdd,
      insPointElement: s.InsPoint,
      getItemElementAt: i => s.ItemAt(i),
    };
  }
}

window.customElements.define('image-editable-list', ImageEditableList);

/**
 * Item of editable image-list control.
 */
class ImageEditableListItem extends ImageListItem {
  get busy() {
    return this.hasAttribute('busy');
  }

  set busy(v) {
    v ? this.setAttribute('busy', 'true') : this.removeAttribute('busy');
  }

  static get markup() {
    return `
      <div class='ratio-box'>
        <img class='image'></div>
        <image-close-button></image-close-button>
      </div>
      <div class='loading-bar'></div>
      <div class="loader blasting-ripple"></div>
    `;
  }

  onClick(ev) {
    if (ev.target.closest('image-close-button') == this.closeButton) {
      this.dispatchEvent(new Event('close'));
      return;
    }
    super.onClick(ev);
  }

  static get selectors() {
    return Object.assign({}, super.selectors, {
      CloseButton: 'image-close-button',
    });
  }

  static get elements() {
    return {
      closeButton: this.selectors.CloseButton,
    };
  }
}

window.customElements.define('image-editable-list-item', ImageEditableListItem);

export { ImageEditableList, ImageEditableListItem };
